import { navigateTo, patternRoute, simpleRoute } from '../../common/frontend/route-utils.js'
import { t } from '../../common/i18n.js'
import { Spinner } from '../components/spinner/spinner.js'
import { canManageTimeAway, hasAnyAdmin, hasPerm } from '../portal-role.js'
import { MenuItem, View } from '../types.js'
import { loadPortalRoleIfNeeded, refreshActiveCampaign } from '../utils/load-utils.js'
import { EmployeeProfileRoute } from '../views/portal/employee-profile-route.js'
import { PeopleRoute } from '../views/portal/people-route.js'
import { CalendarView } from '../views/portal/views/calendar/calendar-view.js'
import { DashboardView } from '../views/portal/views/dashboard/dashboard-view.js'
import { buildQ12Banner } from '../views/portal/views/dashboard/q12-banner.js'
import { EventsView } from '../views/portal/views/events/events-view.js'
import { ReportsView } from '../views/portal/views/reports/reports-view.js'
import { allowedSections, tabForSegment } from '../views/portal/views/settings/settings-section.js'
import { SettingsViewWired } from '../views/portal/views/settings/settings-view-wired.js'

// Shared render for /settings and /settings/:tab. The active tab comes from the
// URL segment; an unknown or unpermitted segment falls back to the first tab
// the held admin keys allow (SettingsView itself also coerces defensively).
const renderSettings = (view: View, segment: string | undefined) => {
    const portalRole = loadPortalRoleIfNeeded(view).data
    if (!portalRole) {
        return <Spinner />
    }
    // Any admin capability opens settings; reached without one only by typing
    // the URL — bounce back to the hub.
    if (!hasAnyAdmin(portalRole.permissions)) {
        navigateTo('/')
        return null
    }
    const sections = allowedSections(portalRole.permissions)
    const activeTab = tabForSegment(segment) ?? sections[0]?.tab ?? 'general'
    return (
        <SettingsViewWired
            lang={view.state.lang}
            permissions={portalRole.permissions}
            activeTab={activeTab}
        />
    )
}

// The portal IS the unified app shell. Its views are mounted at the root URL
// namespace (`/`, `/people`, `/calendar`, …) — there is no `/portal` prefix.
// Login now lands directly on the root dashboard (`/`); the legacy `/portal`
// path is kept only as a redirect to `/` for old bookmarks and a few in-app
// links (e.g. playtime survey).
class PortalModel {
    public dashboardRoute = simpleRoute<View>('/', {
        onEnter: (view) => {
            loadPortalRoleIfNeeded(view)
            // Re-check the Q12 period on every dashboard visit so the banner
            // reflects a submission or a passed deadline without a reload.
            void refreshActiveCampaign(view)
        },
        render: (view) => {
            const portalRole = loadPortalRoleIfNeeded(view).data
            if (!portalRole) {
                return <Spinner />
            }
            return (
                <DashboardView
                    lang={view.state.lang}
                    userName={view.state.user?.name ?? t.common.userFallback(view.state.lang)}
                    isAdmin={hasAnyAdmin(portalRole.permissions)}
                    banner={buildQ12Banner(view.state)}
                />
            )
        },
    })

    // Legacy redirect — tasks now live at /me/tasks.
    public tasksRoute = simpleRoute<View>('/tasks', {
        onEnter: () => navigateTo('/me/tasks'),
        render: () => <Spinner />,
    })

    public eventsRoute = simpleRoute<View>('/events', {
        render: (view) => <EventsView lang={view.state.lang} />,
    })

    public peopleRoute = simpleRoute<View>('/people', {
        onEnter: (view) => {
            loadPortalRoleIfNeeded(view)
        },
        render: (view) => <PeopleRoute view={view} />,
    })

    public peopleProfileRoute = patternRoute<View, '/people/:id'>('/people/:id', {
        onEnter: (view) => {
            loadPortalRoleIfNeeded(view)
        },
        render: (view, params) => (
            <EmployeeProfileRoute view={view} employeeId={Number(params.id)} />
        ),
    })

    public calendarRoute = simpleRoute<View>('/calendar', {
        onEnter: (view) => {
            loadPortalRoleIfNeeded(view)
        },
        render: (view) => {
            const portalRole = loadPortalRoleIfNeeded(view).data
            if (!portalRole) {
                return <Spinner />
            }
            return (
                <CalendarView
                    view={view}
                    lang={view.state.lang}
                    myEmployeeId={portalRole.myEmployeeId ?? null}
                    isManager={canManageTimeAway(portalRole.timeAwayManageScope)}
                    timeAwayScope={portalRole.timeAwayManageScope}
                />
            )
        },
    })

    // Legacy redirect — documents now live at /me/documents (preserve any
    // Google Drive OAuth callback query params).
    public documentsRoute = simpleRoute<View>('/documents', {
        onEnter: () => navigateTo('/me/documents' + window.location.search),
        render: () => <Spinner />,
    })

    public reportsRoute = simpleRoute<View>('/reports', {
        onEnter: (view) => {
            loadPortalRoleIfNeeded(view)
        },
        render: (view) => {
            const portalRole = loadPortalRoleIfNeeded(view).data
            if (!portalRole) {
                return <Spinner />
            }
            if (!hasPerm(portalRole.permissions, 'workspace', 'access_reports')) {
                navigateTo('/')
                return null
            }
            return (
                <ReportsView
                    lang={view.state.lang}
                    canViewPayroll={hasPerm(
                        portalRole.permissions,
                        'workspace',
                        'create_shared_reports',
                    )}
                />
            )
        },
    })

    // Legacy redirect — the audit log now lives as a settings sub-page.
    public auditLogRoute = simpleRoute<View>('/audit-log', {
        onEnter: () => navigateTo('/settings/audit-log'),
        render: () => <Spinner />,
    })

    // Bare /settings renders the first tab the held admin keys allow (rendered
    // directly rather than redirected — a redirect during render drops the
    // nested route update on client-side navigation and leaves a blank screen).
    public settingsRoute = simpleRoute<View>('/settings', {
        onEnter: (view) => {
            loadPortalRoleIfNeeded(view)
        },
        render: (view) => renderSettings(view, undefined),
    })

    public settingsTabRoute = patternRoute<View, '/settings/:tab'>('/settings/:tab', {
        onEnter: (view) => {
            loadPortalRoleIfNeeded(view)
        },
        render: (view, params) => renderSettings(view, params.tab),
    })

    // Legacy `/portal` entry point — redirects to the Hub home page.
    public legacyRoute = simpleRoute<View>('/portal', {
        onEnter: () => {
            navigateTo('/')
        },
        render: () => <Spinner />,
    })

    public menuItem: MenuItem = {
        text: t.portal.menuLabel,
        url: this.dashboardRoute.toUrl(),
        matchingRoutes: [this.dashboardRoute, this.legacyRoute],
    }
}

export const portalModel = new PortalModel()
